// Engineer sign-off for SAFed stress tests (Session BDB). Who may run which stress test is the Director's decision, so NOBODY is
// switched on until the office does it here, per drive. A stress test cannot start until an engineer on the job is signed off for
// its drive. Switching on or off is a two-step confirm: it decides who may load a safety device to its limit.
import { useCallback, useEffect, useState } from "react";
import { api } from "../../../../lib/api";
import { ConfirmButton } from "../../../../components/ConfirmButton";
import type { SafedSignOffView } from "../../../../lib/safedFlowTypes";
import { cardCls, errorText } from "./safedUi";
import { initialMobileLayout } from "../../../../lib/mobileLayout";
import { MobileCardList, MobileListCard } from "../../../../components/MobileListCard";

const GROUP_LABEL: Record<"traction" | "hydraulic", string> = { traction: "Traction", hydraulic: "Hydraulic" };

export function SafedSignOffPanel() {
  const [data, setData] = useState<{ groups: ("traction" | "hydraulic")[]; engineers: { id: string; name: string }[]; signoffs: SafedSignOffView[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Below md the engineers render as cards, chosen once at mount (src/lib/mobileLayout.ts).
  const [mobileCards] = useState(initialMobileLayout);

  const load = useCallback(async () => {
    try {
      setData(await api.safedFlow.signoffs());
      setError(null);
    } catch {
      setError("Could not load who is signed off.");
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function toggle(engineerId: string, testGroup: "traction" | "hydraulic", on: boolean) {
    try {
      await api.safedFlow.setSignoff({ engineerId, testGroup, on });
      await load();
    } catch (err) {
      setError(errorText(err, "Could not change that."));
    }
  }

  // One switch, written once for the table cell and the mobile card.
  const toggleFor = (engineerId: string, g: "traction" | "hydraulic") => {
    const on = data?.signoffs.some((s) => s.engineerId === engineerId && s.testGroup === g) ?? false;
    return (
      <ConfirmButton
        label={on ? "Signed off. Switch off" : "Not signed off. Switch on"}
        confirmLabel={on ? "Confirm: switch off" : "Confirm: switch on"}
        className={`min-h-11 px-2 text-xs ${on ? "text-ele-green" : "text-ele-muted"}`}
        onConfirm={() => void toggle(engineerId, g, !on)}
      />
    );
  };

  return (
    <details className="mt-4">
      <summary className="min-h-11 cursor-pointer py-2 font-heading text-sm uppercase tracking-wide text-ele-strong">Engineers signed off for stress tests</summary>
      <div className={`mt-2 ${cardCls}`}>
        <p className="text-sm text-ele-body">
          Nobody is signed off until you switch them on. A stress test cannot start until an engineer on the job is signed off for that drive. Who may run
          stress tests is the Director&apos;s decision.
        </p>
        {error && <p role="alert" className="mt-2 text-sm text-ele-danger">{error}</p>}
        {data && data.engineers.length === 0 && <p className="mt-2 text-sm text-ele-muted">There are no engineers who can be signed off yet.</p>}
        {data && data.engineers.length > 0 && mobileCards && (
          /* Below md the table cannot hold two long switch labels beside a name. Each engineer is a
             card with one labelled switch per drive. */
          <div className="mt-3">
            <MobileCardList label="Engineers signed off for stress tests">
              {data.engineers.map((e) => (
                <MobileListCard
                  key={e.id}
                  primary={e.name}
                  actions={data.groups.map((g) => (
                    <div key={g} className="flex w-full flex-wrap items-center justify-between gap-2">
                      <span className="text-sm text-ele-body">{GROUP_LABEL[g]}</span>
                      {toggleFor(e.id, g)}
                    </div>
                  ))}
                />
              ))}
            </MobileCardList>
          </div>
        )}
        {data && data.engineers.length > 0 && !mobileCards && (
          <table className="ele-list-table mt-3 w-full text-sm">
            <thead>
              <tr className="text-left text-ele-muted">
                <th>Engineer</th>
                {data.groups.map((g) => (
                  <th key={g}>{GROUP_LABEL[g]}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.engineers.map((e) => (
                <tr key={e.id}>
                  <td>{e.name}</td>
                  {data.groups.map((g) => (
                    <td key={g}>{toggleFor(e.id, g)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </details>
  );
}
